import { useEffect, useState, useRef } from "react";
import { DataGrid, Column, Pager, Paging } from "devextreme-react/data-grid";

import { GoAiguaButton, SearchBox } from "components/generic";
import GoAiguaTextInput from "components/generic/goaiguatextinput";

import { useIntl } from "react-intl";

import { Dispatch } from "redux";
import { connect, RootStateOrAny, useSelector } from "react-redux";

import { useKeycloak } from "@react-keycloak/web";

import UserCreateModal from "components/adhoc/usercreatemodal";
import UsersTableAction from "components/adhoc/userstableaction";

import { useDelayedState } from "utils/hooks";
import { useRequestStatusManager } from "utils/hooks/use-request-status-manager.hook";
import { makeUsersPageTabFunctions } from "utils/tabs/users-page-tab-functions";
import ToastRequestHandler from "components/adhoc/toast-request-handler";
import {
    getEnvVariable,
    REACT_APP_CREATE_USERS_ENABLED,
} from "utils/environment";

const isTokenExpired = (token: any) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split("")
            .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join("")
    );

    const { iat } = JSON.parse(jsonPayload);
    // 5 min from generation
    const expired = Date.now() >= (iat + 5 * 60) * 1000;
    return expired;
};

const UsersPageUsersTab = (props: { token: string }) => {
    const intl = useIntl();
    const dateFormat: string = useSelector(
        (state: RootStateOrAny) => state.preferences.timeDateFormat.dateFormat
    );
    const dateFormatRef = useRef<string>();
    dateFormatRef.current = dateFormat;

    const dataGridRef = useRef<DataGrid>(null);

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [datasource, setDatasource] = useState<any>(null);
    const [textFilter, displayedTextFilter, setTextFilter] = useDelayedState(
        "",
        500
    );

    const {
        allContentIsReady,
        errorList,
        success,
        setError,
        subscribeContent,
        unsubscribeContent,
        removeAllData,
    } = useRequestStatusManager();

    const contentName = "adminPage";

    const {
        keycloak,
    }: {
        initialized: boolean;
        keycloak: Keycloak.KeycloakInstance;
    } = useKeycloak();

    const {
        createDatasource,
        dateParsedToLocale,
        defaultValueFormatter,
        renderStatusLabel,
    } = makeUsersPageTabFunctions({
        contentName,
        dateFormatRef,
        intl,
        setDatasource,
        setError,
        textFilter,
        unsubscribeContent,
    });

    useEffect(() => {
        if (isTokenExpired(props.token)) {
            window.location.reload();
            // keycloak.logout();
        }
    }, [props.token, displayedTextFilter, isModalOpen]);

    useEffect(() => {
        subscribeContent(contentName);
        createDatasource();

        return () => unsubscribeContent(contentName);
    }, []);

    useEffect(() => {
        subscribeContent(contentName);
        setTimeout(() => {
            createDatasource();
        });

        return () => unsubscribeContent(contentName);
    }, [textFilter]);

    useEffect(() => {
        setDatasource(null);
    }, [displayedTextFilter]);

    return (
        <div className="l-users-page-users-tab">
            <div className="l-users-page-users-tab__container">
                <div className="l-users-page-users-tab__table-header">
                    <div className="l-users-page-users-tab__input-group --text-filter">
                        <label>{`${intl.formatMessage({
                            id: "app.general.administration_platform",
                        })}`}</label>
                        <SearchBox
                            placeholder={`${intl.formatMessage({
                                id: "app.general.search",
                            })}...`}
                            className="l-users-page-users-tab__input-group__input"
                            value={displayedTextFilter}
                            onChange={setTextFilter}
                        />
                    </div>
                    {getEnvVariable(REACT_APP_CREATE_USERS_ENABLED) ===
                        "true" && (
                        <div className="l-users-page-users-tab__create-user-button">
                            <GoAiguaButton
                                text={`${intl.formatMessage({
                                    id: "app.users.create_users",
                                })}`}
                                onClick={() => setIsModalOpen(true)}
                            />
                        </div>
                    )}
                </div>
                <div className={"l-users-page-users-tab__table-body"}>
                    <div
                        className={
                            "ag-theme-alpine-dark goaigua-table fit-parent border-box"
                        }
                    >
                        <DataGrid
                            rowAlternationEnabled={true}
                            showBorders={false}
                            dataSource={datasource !== null ? datasource : []}
                            remoteOperations={true}
                            showRowLines={false}
                            showColumnLines={true}
                            scrolling={{ mode: "standard" }}
                            columnHidingEnabled={true}
                            ref={dataGridRef}
                            height={"100%"}
                            loadPanel={undefined}
                            sorting={{
                                mode: "none",
                            }}
                            noDataText={""}
                        >
                            <Paging defaultPageSize={15} />
                            <Pager
                                visible={true}
                                allowedPageSizes={[15, 30, 50]}
                                displayMode={"full"}
                                showPageSizeSelector={true}
                                showInfo={false}
                            />
                            <Column
                                dataField="enabled"
                                caption={intl.formatMessage({
                                    id: "app.users.status",
                                })}
                                cellRender={renderStatusLabel}
                                width={"100"}
                            ></Column>
                            <Column
                                dataField="firstName"
                                caption={intl.formatMessage({
                                    id: "app.users.first_name",
                                })}
                                customizeText={defaultValueFormatter}
                            ></Column>
                            <Column
                                dataField="lastName"
                                caption={intl.formatMessage({
                                    id: "app.users.last_name",
                                })}
                                customizeText={defaultValueFormatter}
                            ></Column>
                            <Column
                                dataField="username"
                                caption={intl.formatMessage({
                                    id: "app.users.username",
                                })}
                                customizeText={defaultValueFormatter}
                            ></Column>
                            <Column
                                dataField="email"
                                caption={intl.formatMessage({
                                    id: "app.users.email_address",
                                })}
                                customizeText={defaultValueFormatter}
                            ></Column>
                            <Column
                                dataField="creationDate"
                                caption={intl.formatMessage({
                                    id: "app.users.creation_date",
                                })}
                                customizeText={dateParsedToLocale}
                            ></Column>
                            <Column
                                caption={intl.formatMessage({
                                    id: "app.users.actions",
                                })}
                                cellRender={(params) => (
                                    <UsersTableAction {...params} />
                                )}
                            ></Column>
                        </DataGrid>
                    </div>
                </div>
            </div>
            <UserCreateModal
                subscribeContent={subscribeContent}
                unsubscribeContent={unsubscribeContent}
                setError={setError}
                isOpen={isModalOpen}
                setIsOpen={setIsModalOpen}
            />

            <ToastRequestHandler
                allContentIsReady={allContentIsReady}
                closeToast={removeAllData}
                errorList={errorList}
                success={success}
            />
        </div>
    );
};

const mapStateToProps = (state: any) => {
    return {
        token: state.keycloak.token,
    };
};
const mapDispatchToProps = (dispatch: Dispatch) => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersPageUsersTab);
